/**
 * useHighlights Hook
 *
 * Custom hook for managing article highlights including creation,
 * retrieval, deletion, and detachment detection.
 */

import { useState, useCallback, useEffect } from 'react';
import {
  createHighlight as createHighlightAPI,
  getArticleHighlights,
  deleteHighlight as deleteHighlightAPI,
  deleteAllHighlights as deleteAllHighlightsAPI,
  checkHighlightDetachment,
  reattachHighlights as reattachHighlightsAPI,
} from '../services/highlights';

/**
 * Calculate SHA-256 hash for summary version tracking
 * @param {string} text - Text to hash
 * @returns {Promise<string>} Hex string of hash
 */
async function calculateSummaryHash(text) {
  const encoder = new TextEncoder();
  const data = encoder.encode(text || '');
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Normalize text for comparison by collapsing whitespace and removing markdown syntax
 * @param {string} text - Text to normalize
 * @returns {string} Normalized text
 */
function normalizeText(text) {
  return text
    .replace(/\*\*(.*?)\*\*/g, '$1') // Remove bold markers (**text** -> text)
    .replace(/）\s+(\d+\.)/g, '）$1') // Remove space/newline between Chinese paren and numbered list: ）\n1. -> ）1.
    .replace(/。\s+(\d+\.)/g, '。$1') // Remove space/newline between period and numbered list: 。\n2. -> 。2.
    .replace(/\s*[-*+]\s+/g, ' ') // Remove markdown list markers (-, *, +) with surrounding spaces
    .replace(/\s*\d+\.\s+/g, ' ') // Remove numbered list markers (1. 2. 3. etc.) with surrounding spaces
    .replace(/\s+/g, ' ') // Collapse all whitespace (including newlines) to single spaces
    .replace(/\s*•/g, '•') // Remove ALL whitespace before bullet points (•) - MUST be after general whitespace collapse
    .trim();
}

/**
 * Extract text position in summary content
 * @param {string} summaryText - Full summary text
 * @param {string} selectedText - Selected text to find
 * @returns {Object|null} Position object with start and end, or null if not found
 */
function findTextPosition(summaryText, selectedText) {
  if (!summaryText || !selectedText) {
    return null;
  }

  // Try exact match first
  let start = summaryText.indexOf(selectedText);
  if (start !== -1) {
    return {
      start,
      end: start + selectedText.length,
    };
  }

  // Try normalized match - extract the first few words to find the approximate location
  const normalizedSummary = normalizeText(summaryText);
  const normalizedSelected = normalizeText(selectedText);

  // console.log('[Highlight Debug] Normalized selected:', normalizedSelected.substring(0, 100));
  // console.log('[Highlight Debug] Normalized summary length:', normalizedSummary.length);
  // console.log('[Highlight Debug] Does normalized summary contain normalized selected?', normalizedSummary.includes(normalizedSelected.substring(0, 50)));

  // Search for the normalized selected text in the normalized summary
  const normalizedStart = normalizedSummary.indexOf(normalizedSelected);
  if (normalizedStart === -1) {
    // console.error('[Highlight Debug] Match not found in normalized text');
    // console.error('[Highlight Debug] First 50 chars found at:', normalizedSummary.indexOf(normalizedSelected.substring(0, 50)));
    return null;
  }

  // Strategy: Find a unique substring from the normalized text in the original
  // Take a chunk of characters that should be unique enough
  const searchLength = Math.min(20, normalizedSelected.length);
  const searchText = normalizedSelected.substring(0, searchLength);

  // console.log('[Highlight Debug] Searching for unique text:', JSON.stringify(searchText));

  // Normalize the summary text for searching
  const normalizedOriginal = normalizeText(summaryText);

  // Find the position in the normalized original text
  const normalizedPos = normalizedOriginal.indexOf(searchText);
  if (normalizedPos === -1) {
    // console.error('[Highlight Debug] Could not find search text in normalized original');
    return null;
  }

  // console.log('[Highlight Debug] Found at normalized position:', normalizedPos);

  // Simple approach: find the actual position by searching for the text directly
  // Get first few actual characters from selected text (not normalized)
  const selectedStart = selectedText.trim().substring(0, 15);
  const actualStart = summaryText.indexOf(selectedStart);

  if (actualStart !== -1) {
    // Found start position, now find end position
    // Search for the last few characters to find the actual end
    const selectedEnd = selectedText.trim().slice(-15);
    const searchStartPos = actualStart + 10; // Start searching a bit after the start
    const actualEndMatch = summaryText.indexOf(selectedEnd, searchStartPos);

    if (actualEndMatch !== -1) {
      const actualEnd = actualEndMatch + selectedEnd.length;
      // console.log('[Highlight Debug] Found exact substring match at:', actualStart, '-', actualEnd);
      // console.log('[Highlight Debug] Matched end text:', JSON.stringify(selectedEnd));
      return {
        start: actualStart,
        end: actualEnd
      };
    } else {
      // Fallback to length-based calculation if we can't find the end
      const actualEnd = actualStart + selectedText.length;
      // console.log('[Highlight Debug] Found start but not end, using length calculation:', actualStart, '-', actualEnd);
      return {
        start: actualStart,
        end: actualEnd
      };
    }
  }

  // Fallback: search for normalized chunks in original text
  // This handles the case where whitespace differs
  const words = normalizedSelected.split(/\s+/).filter(w => w.length > 1);
  const firstFewWords = words.slice(0, 3).join('');
  const lastFewWords = words.slice(-3).join('');

  // console.log('[Highlight Debug] Searching for word sequence:', firstFewWords);

  const startPos = summaryText.indexOf(firstFewWords);
  if (startPos === -1) {
    // console.error('[Highlight Debug] Could not find word sequence in original text');
    return null;
  }

  const endSearchStart = startPos + firstFewWords.length;
  const endPos = summaryText.indexOf(lastFewWords, endSearchStart);
  const finalEnd = endPos !== -1 ? endPos + lastFewWords.length : summaryText.length;

  // console.log('[Highlight Debug] Final positions - start:', startPos, 'end:', finalEnd);
  // console.log('[Highlight Debug] Text at position:', summaryText.substring(startPos, Math.min(startPos + 100, summaryText.length)));

  return {
    start: startPos,
    end: finalEnd,
  };
}

/**
 * Extract context around selected text
 * @param {string} fullText - Full text content
 * @param {number} start - Start position
 * @param {number} end - End position
 * @param {number} contextLength - Length of context to extract (default: 50)
 * @returns {Object} Object with prefix and suffix context
 */
function extractContext(fullText, start, end, contextLength = 50) {
  const prefix = fullText.substring(Math.max(0, start - contextLength), start);
  const suffix = fullText.substring(end, Math.min(fullText.length, end + contextLength));

  return { prefix, suffix };
}

export function useHighlights(articleId, summaryText) {
  const [highlights, setHighlights] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [detachmentInfo, setDetachmentInfo] = useState(null);
  const [error, setError] = useState(null);

  /**
   * Fetch highlights for the current article
   */
  const fetchHighlights = useCallback(async () => {
    if (!articleId) return;

    try {
      setIsLoading(true);
      setError(null);
      const response = await getArticleHighlights(articleId, false);
      setHighlights(response.highlights || []);
    } catch (err) {
      console.error('Error fetching highlights:', err);
      setError(err.message);
      setHighlights([]);
    } finally {
      setIsLoading(false);
    }
  }, [articleId]);

  /**
   * Check for detached highlights
   */
  const checkDetachment = useCallback(async () => {
    if (!articleId) return;

    try {
      const response = await checkHighlightDetachment(articleId);
      setDetachmentInfo(response);
    } catch (err) {
      console.error('Error checking detachment:', err);
    }
  }, [articleId]);

  /**
   * Create a new highlight
   * @param {string} selectedText - The exact text being highlighted
   * @param {Object} selectionRange - Selection range with start/end positions
   * @returns {Promise<Object>} Created highlight
   */
  const createHighlight = useCallback(
    async (selectedText, selectionRange = null, renderedText = null) => {
      if (!articleId || !summaryText || !selectedText) {
        throw new Error('Missing required data for highlight creation');
      }

      // Use rendered text if provided, otherwise fall back to summary text
      const textForPositioning = renderedText || summaryText;

      try {
        setIsCreating(true);
        setError(null);

        // Calculate summary hash for version tracking
        const summaryHash = await calculateSummaryHash(summaryText);

        // Find text position if not provided
        let position = selectionRange;
        if (!position) {
          // console.log('[createHighlight] selectedText:', selectedText.substring(0, 100));
          // console.log('[createHighlight] selectedText has bold?', selectedText.includes('**'));
          // console.log('[createHighlight] textForPositioning (first 200):', textForPositioning.substring(0, 200));

          position = findTextPosition(textForPositioning, selectedText);
          if (!position) {
            // console.log('[createHighlight] ❌ Could not find position');
            // console.log('[createHighlight] Normalized selected:', normalizeText(selectedText).substring(0, 100));
            // console.log('[createHighlight] Normalized rendered:', normalizeText(textForPositioning).substring(0, 100));
            throw new Error('Could not find selected text in rendered content');
          }

          // console.log('[createHighlight] Found position:', position);
          // console.log('[createHighlight] Text at position:', textForPositioning.substring(position.start, Math.min(position.start + 100, textForPositioning.length)));
        }

        // Extract context around selection
        const context = extractContext(textForPositioning, position.start, position.end);

        // Build W3C Web Annotation structure with multi-selector strategy
        const annotation = {
          '@context': 'http://www.w3.org/ns/anno.jsonld',
          type: 'Annotation',
          body: {
            type: 'TextualBody',
            value: selectedText,
            format: 'text/plain',
          },
          target: {
            selector: [
              // RangeSelector (fast, fragile)
              {
                type: 'RangeSelector',
                startSelector: {
                  type: 'XPathSelector',
                  value: '/html/body/div/div/div',
                },
                endSelector: {
                  type: 'XPathSelector',
                  value: '/html/body/div/div/div',
                },
              },
              // TextPositionSelector (fast, moderately fragile)
              {
                type: 'TextPositionSelector',
                start: position.start,
                end: position.end,
              },
              // TextQuoteSelector (slow, robust)
              {
                type: 'TextQuoteSelector',
                exact: selectedText,
                prefix: context.prefix,
                suffix: context.suffix,
              },
            ],
          },
        };

        // Create highlight via API
        const highlightData = {
          annotation,
          exact_text: selectedText,
          summary_version: summaryHash,
        };

        const newHighlight = await createHighlightAPI(articleId, highlightData);

        // Add to local state
        setHighlights((prev) => [...prev, newHighlight]);

        return newHighlight;
      } catch (err) {
        console.error('Error creating highlight:', err);
        setError(err.message);
        throw err;
      } finally {
        setIsCreating(false);
      }
    },
    [articleId, summaryText]
  );

  /**
   * Delete a highlight
   * @param {number} highlightId - Highlight ID to delete
   */
  const deleteHighlight = useCallback(async (highlightId) => {
    try {
      setError(null);
      await deleteHighlightAPI(highlightId);

      // Remove from local state
      setHighlights((prev) => prev.filter((h) => h.id !== highlightId));
    } catch (err) {
      console.error('Error deleting highlight:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Delete all highlights for the current article
   */
  const deleteAllHighlights = useCallback(async () => {
    if (!articleId) return;

    try {
      setError(null);
      await deleteAllHighlightsAPI(articleId);

      // Clear local state
      setHighlights([]);
    } catch (err) {
      console.error('Error deleting all highlights:', err);
      setError(err.message);
      throw err;
    }
  }, [articleId]);

  /**
   * Re-attach detached highlights
   * @param {number} similarityThreshold - Minimum similarity threshold (0.0-1.0)
   * @returns {Promise<Object>} Re-attachment results
   */
  const reattachHighlights = useCallback(
    async (similarityThreshold = 0.8) => {
      if (!articleId) return;

      try {
        setError(null);
        const response = await reattachHighlightsAPI(articleId, similarityThreshold);

        // Refresh highlights after re-attachment
        await fetchHighlights();
        await checkDetachment();

        return response;
      } catch (err) {
        console.error('Error re-attaching highlights:', err);
        setError(err.message);
        throw err;
      }
    },
    [articleId, fetchHighlights, checkDetachment]
  );

  // Fetch highlights when article changes
  useEffect(() => {
    if (articleId) {
      fetchHighlights();
      checkDetachment();
    }
  }, [articleId, fetchHighlights, checkDetachment]);

  return {
    highlights,
    isLoading,
    isCreating,
    error,
    detachmentInfo,
    createHighlight,
    deleteHighlight,
    deleteAllHighlights,
    fetchHighlights,
    reattachHighlights,
    checkDetachment,
  };
}
